import React from 'react';
import { Home, LayoutDashboard, Landmark, GraduationCap, Stethoscope, Briefcase, GitMerge, PiggyBank, Users, Calculator, HardHat, Boxes, ShoppingCart, ClipboardList, LifeBuoy, BookOpen, Truck, Megaphone, ListChecks } from 'lucide-react';
import { isUsuarioPresoLoja, lojaNoPilotoChamados } from '../utils/escopoUsuario';
import { getConfigRestrito } from '../utils/perfisRestritos';
import { alcancaModulo, temAcessoTotal } from '../utils/perfisDerivados';
import { PERFIS_MARKETING } from '../utils/perfisMarketing';
import { podeVerProjetos } from '../utils/acessoProjetos';

// =====================================================================
// menu-itens.js — QUAIS MÓDULOS este usuário alcança (a regra do menu)
// =====================================================================
// ⚠️⚠️ SAIU DE DENTRO DO `Sidebar.js` em 11/09/2026, em dois passos e por dois
//    motivos diferentes — vale registrar os dois:
//
//    1. A **Home** passou a precisar da MESMA resposta, para saber para onde
//       mandar quem não tem nenhuma aba (`rotaInicialDoUsuario` em
//       `home/abas-home.js`). A alternativa era reescrever estas ~20 condições
//       num segundo arquivo — que é exatamente como nasce um menu que mostra um
//       módulo e um redirecionamento que leva a outro, sem erro em lugar nenhum.
//    2. ⚠️ Num ARQUIVO PRÓPRIO, e não exportado do `Sidebar.js`, porque o Sidebar
//       importa `react-router-dom` (o `NavLink`) — e isso arrastava o roteador
//       para dentro do teste de `abas-home`, que é código PURO. O teste quebrou
//       na hora. Regra que fica: regra de acesso não mora junto de componente.
//
// ⚠️ Depende SÓ do `user` — nada de estado de componente. É o que a mantém
//    chamável de fora do React.

/**
 * @param {object} user  o usuário do `localStorage`
 * @param {Set<string>} caps  as capacidades dele (`src/utils/capacidades.js`).
 *   ⚠️ OPCIONAL, e continua opcional de propósito: esta função é PURA e é
 *   chamada de fora do React (o `primeiraRotaDoUsuario`, que a Home usa, e os
 *   testes de `abas-home`). Recebê-las por ARGUMENTO, em vez de buscá-las aqui
 *   dentro, é o que a mantém pura — buscar aqui arrastaria `fetch` para dentro
 *   de código testado sem rede.
 *   ⚠️ Sem elas, vale só a lista de perfis: é o comportamento de antes de
 *   18/09/2026, que é a falha certa (nada se abre por engano).
 */
export function itensDeMenu(user, caps = new Set()) {
const userRole = user ? user.perfil : null;

// ⚠️ Os PERFIS DERIVADOS (a Diretoria segmentada por área em 2026-08-19) entram
// no menu por `alcancaModulo(userRole, '<módulo>')`, nunca por nome escrito à
// mão: o menu é global e cada derivado alcança um recorte diferente — traduzir
// o perfil aqui entregaria a todos eles o portal inteiro. Fonte única do
// recorte: utils/perfisDerivados.js (espelho de server/perfis-derivados.js, que
// é a trava de verdade). Perfil derivado novo aparece no menu sozinho.
const isAdmin = userRole === 'Admin';
// Usuário de loja (ex.: Linhares) não vê módulos centrais.
const presoLoja = isUsuarioPresoLoja(user);
// CanSeeAnalises = userRole === 'Dados' || isAdmin || userRole === 'Diretoria' ;
// ⚠️ Os GERENTES entraram aqui em 2026-08-28 pelo Registro de Nulos em Progresso
//    e saíram no MESMO dia, quando o sub-módulo mudou para o Financeiro — que já
//    os enxerga (ver `canSeeFinanceiro`). Tirá-los é o certo: sem os nulos não
//    sobra aba nenhuma que a loja alcance neste hub, e o item de menu levaria a
//    uma tela vazia.
// ⚠️ `CanSeeAuditoria`/o item de menu "/auditoria" saíram em 2026-09-23
// (DEM-00011, Fase 5, T26) — o hub esvaziou na Fase 4 (o único card, o
// Checklist, virou "Auditoria Financeira e de Estoques" no Operacional).
// O perfil `Auditoria` NÃO ficou órfão: segue alcançando Nulos (Financeiro),
// Biblioteca e agora Checklist/SA em Operacional. `Gerente Adm` nunca teve
// esta entrada de menu (só acesso de rota, nunca exposto aqui) — perde só o
// que nunca usava por aqui.
// Módulo Fiscal: substitui o antigo hub de Automações. Barra usuário preso à loja.
const canSeeFiscal = (userRole === 'Fiscal' || userRole === 'Gerente Adm' || isAdmin || userRole === 'Diretoria') && !presoLoja;
const canSeePainelAdmin = isAdmin;
// Módulos vindos da antiga "Central de Formulários" (Forms.js), agora separados.
// RH entra no Treinamento pelo PPI (migrado do RH em 2026-07-15): opera Processos
// & Vagas + Triagem. Dentro do módulo, o RH só vê o card do PPI (o de Formulários
// exclui RH).
const canSeeTreinamento = userRole === 'Treinamento' || isAdmin || userRole === 'Diretoria' || userRole === 'RH' || alcancaModulo(userRole, 'treinamento') || temAcessoTotal(userRole, 'treinamento');
const canSeeMedicina = userRole === 'Medico' || userRole === 'RH' || userRole === 'Gerente' || isAdmin || userRole === 'Diretoria' || userRole === 'Manutencao' || alcancaModulo(userRole, 'medicina') || temAcessoTotal(userRole, 'medicina');
const canSeeProcessoSeletivo =  (userRole === 'RH' ||  userRole === 'ProcessoSeletivo' || isAdmin || userRole === 'Diretoria' || userRole === 'Medico' || alcancaModulo(userRole, 'processoseletivo')) && !presoLoja;
const canSeeFinanceiro = userRole === 'Admin' || userRole === 'Financeiro' || userRole === 'Gerente'  || userRole === 'Gerente Plantao' ||userRole === 'Gerente Adm' || userRole === 'Diretoria' || userRole === 'Auditoria' || alcancaModulo(userRole, 'financeiro') || temAcessoTotal(userRole, 'financeiro');
// Módulo Contábil: mesmos perfis que acessavam as automações contábeis no hub. Barra usuário preso à loja.
const canSeeContabil = (userRole === 'Contabil' || userRole === 'Gerente Adm' || userRole === 'Fiscal' || isAdmin || userRole === 'Diretoria') && !presoLoja;
// Módulo Manutenção (Programa de Certificação de Equipamentos). Perfis Manutencao/
// Diretoria veem o módulo inteiro (não presos à loja). O GERENTE também entra —
// cai na Validação de Certificação da própria loja —, por isso ele NÃO é barrado
// pelo presoLoja (é justamente quem deve validar o certificado da sua loja).
const canSeeManutencao = userRole === 'Gerente'
  || ((userRole === 'Manutencao' || isAdmin || userRole === 'Diretoria' || alcancaModulo(userRole, 'manutencao') || temAcessoTotal(userRole, 'manutencao')) && !presoLoja);
// Módulo Compras: perfil "Compras" gerencia; Manutencao (quem pediu as peças)
// e Diretoria acompanham. Barra usuário preso à loja.
// O GERENTE também entra (2026-08-17) — acompanha as peças solicitadas para a
// PRÓPRIA loja, que é o assunto do e-mail que ele passou a receber. Como no
// Manutenção, ele fica FORA do presoLoja: ser preso à loja é o que ele é, e o
// recorte acontece no backend (server-compras.js › recorteLoja).
// ⚠️ O FISCAL também entra (2026-09-03): a devolutiva da nota fiscal é um dos
// seis passos da fila de Solicitação de Reparo, e ela mora na tela do
// Compras. Dentro do módulo ele alcança só aquele card.
const canSeeCompras = userRole === 'Gerente'
  || ((userRole === 'Compras' || userRole === 'Manutencao' || userRole === 'Fiscal' || isAdmin || userRole === 'Diretoria' || alcancaModulo(userRole, 'compras')) && !presoLoja);
// Módulo LOGÍSTICA (2026-09-10): a Retirada de Equipamento, que era um card
// do Manutenção e uma aba do Chamados. Quem retira é o TI e a Manutenção;
// quem PEDE que levem é o Gerente da loja.
// ⚠️ O GERENTE fica FORA do presoLoja, como no Manutenção/Compras/Chamados:
// ser preso à loja é o que ele é, e é justamente dele o ato de registrar a
// saída. O recorte acontece no backend (server-logistica.js › escopoPorLoja).
// ⚠️ Casamento EXATO em `Gerente TI`: ele NÃO pode vir de um
// startsWith('Gerente'), que engoliria os gerentes de loja (e vice-versa).
// ⚠️ `Logistica` é o perfil novo do módulo e HOJE NÃO TEM NINGUÉM — está
// aqui para funcionar no dia em que tiver, sem exigir deploy.
// ⚠️ `Diretoria Op` vem pelo `alcancaModulo`, que já a cobre — não
// acrescentar o nome dela à mão.
const canSeeLogistica = userRole === 'Gerente'
  || ((userRole === 'Logistica' || userRole === 'Manutencao' || userRole === 'Gerente TI' || userRole === 'TI' || isAdmin || userRole === 'Diretoria' || alcancaModulo(userRole, 'logistica') || temAcessoTotal(userRole, 'logistica')) && !presoLoja);
// Módulo MARKETING (2026-09-16): o Gerador de Arte de Contratação e os
// Aniversariantes do Mês. Nasceu com três perfis (`Marketing`, `Diretoria`,
// `Admin`) e foi ABERTO em 18/09/2026 para a loja e para as áreas de gente e de
// operação — a lista é a `PERFIS_MARKETING`, compartilhada com o App.js.
// ⚠️ O `Marketing` e a `Diretoria Op` NÃO aparecem escritos na lista: são perfis
//    DERIVADOS e chegam pelo `alcancaModulo`, que é a fonte única do recorte.
//    Escrever o nome à mão criaria uma segunda verdade, que envelhece sozinha.
// ⚠️ `Diretoria RH` segue FORA — não alcança o módulo `marketing` em
//    perfisDerivados.js, então o `alcancaModulo` já a barra.
// ⚠️⚠️ O `&& !presoLoja` CAIU JUNTO COM A ABERTURA, e tinha que cair: `Gerente`,
//    `Gerente Plantao` e `Coordenador de Qualidade` SÃO usuários de loja, e essa
//    guarda os barraria em silêncio — o item simplesmente não apareceria no
//    menu, com a rota já liberada. Quem decide agora é só a lista de perfil.
//    ⚠️ Não é descuido de recorte: este módulo não tem recorte por loja em
//    lugar nenhum (nem na rota, nem no backend). Ver o comentário do
//    `/api/marketing/aniversariantes`.
const canSeeMarketing = isAdmin || userRole === 'Diretoria'
    || PERFIS_MARKETING.includes(userRole) || alcancaModulo(userRole, 'marketing');
// Módulo Demandas (2026-09-17): o controle do trabalho da equipe de dados.
// ⚠️ `Admin` (dupla) + `Diretoria` (28/09, que VÊ a fila e ABRE pedido). A fase 3
//    (demais perfis administrativos) ainda não entrou. Quando abrir de vez,
//    trocar por `alcancaModulo(..., 'demandas')`, a fonte única do recorte, em
//    vez de nomes à mão.
// ⚠️ ESTA LINHA ESPELHA o allowedRoles de `/demandas` no App.js e o
//    `PERFIS_VER` de server/demandas/demandas-acesso.js.
const canSeeDemandas = isAdmin || userRole === 'Diretoria';
const canSeeRH = userRole === 'RH' || isAdmin || userRole === 'Diretoria' || alcancaModulo(userRole, 'rh') || temAcessoTotal(userRole, 'rh') || userRole === 'EntrevistaDesligamento' || userRole === 'Gerente' || userRole === 'Gerente Plantao' || userRole === 'Treinamento';
// Módulo Chamados (2026-08-21): a LOJA abre (`Gerente`, `Gerente Plantao`) e o
// TI atende (`Gerente TI` tria, `TI` executa). Substitui o Auvo.
// ⚠️ Os gerentes ficam FORA do presoLoja — ser preso à loja é o que eles são, e
// é justamente deles o ato de abrir e de validar; o recorte é do backend
// (server-chamados.js › recorteLoja). Mesmo desenho do Manutenção e do Compras.
// ⚠️ `Gerente Adm` NÃO entra (decisão da Julia, 21/08/2026): é o INVERSO do
// recorte da agenda, onde é o Plantão que fica de fora. Não unificar por
// analogia — o backend recusa igual.
// ⚠️ Casamento EXATO: `Gerente TI` não pode vir de startsWith('Gerente'), que
// engoliria os gerentes de loja (e vice-versa).
// ⚠️ `Diretoria` entrou em 2026-08-31 (acesso total ao módulo). A `Diretoria Op`
// vem pelo `alcancaModulo`, que já estava aqui — não acrescentar o nome dela.
// ⚠️⚠️ A FASE DE TESTE (31/08/2026, TEMPORÁRIA) entra como um `&&` no fim, e
// não misturada nas condições de perfil: são perguntas diferentes. O perfil
// diz o que a pessoa PODE fazer; o piloto diz se o módulo já CHEGOU na loja
// dela. Fundir as duas faria o desligamento do piloto (`LOJAS_PILOTO = []`)
// ter de mexer numa expressão de permissão — que é o lugar mais caro de
// errar. Ver `src/utils/escopoUsuario.js` › `lojaNoPilotoChamados`.
// ⚠️ `Coordenador de Qualidade` está junto dos gerentes (fora do presoLoja):
// dentro do Chamados ele é equiparado ao Gerente, e estar preso à própria loja
// é o que ele é. ⚠️ Ele é perfil RESTRITO, então o menu dele NÃO usa este
// `show` — vem do allowlist do `perfisRestritos.js` (ver `itensMenu` abaixo,
// que é quem aplica o piloto no caso dele).
// ⚠️⚠️ A PERMISSÃO DO CHAMADOS SAIU DAQUI EM 18/09/2026 e virou a capacidade
//    `chamados.modulo` (`server/acessos/capacidades.js`), concedida pela matriz
//    do Painel Admin — sem deploy. A lista de perfis que estava escrita aqui é
//    hoje o `padrao` dela, com os mesmos nomes.
//    ⚠️ Antes disso, conceder uma ABA do Chamados pela matriz não adiantava: o
//    item nem aparecia no menu, porque QUEM MANDAVA ERA ESTA LINHA. Medido com o
//    perfil `DevOps` no dia em que ele nasceu.
//
// ⚠️ O que NÃO virou capacidade, e não deve virar:
//    · `presoLoja` — quem é de LOJA só alcança o módulo nos perfis de loja; é
//      ESCOPO (quem essa pessoa é), não permissão.
//    · `lojaNoPilotoChamados` — é DISPONIBILIDADE (esta loja já usa o módulo?).
//      Confundir as duas é o erro que o `chamados-piloto.js` documenta inteiro.
const PERFIS_DE_LOJA_CHAMADOS = ['Gerente', 'Gerente Plantao', 'Coordenador de Qualidade'];
const canSeeChamados = caps.has('chamados.modulo')
  && (!presoLoja || PERFIS_DE_LOJA_CHAMADOS.includes(userRole))
  && lojaNoPilotoChamados(user);
// Módulo Operacional (hub de ferramentas da operação). Perfil "Operacional".
// ⚠️ O `Gerente` fica FORA do presoLoja (2026-08-26): ele entrou para CONSULTAR
// a Avaliação BSV da própria loja, e ser preso à loja é o que ele é — o &&
// !presoLoja o barraria. Mesmo desenho do Manutenção/Compras/Chamados.
// ⚠️ Desde 2026-09-03 ele tem DOIS motivos para estar aqui, e são opostos: na
// Avaliação BSV ele só LÊ (quem avalia é o escritório); na Autoavaliação de
// Controles ele é quem PREENCHE, sobre a própria loja. Quem faz o recorte é o
// backend (server-bsv.js e server-autoavaliacao.js), cada um com as suas duas
// listas de perfil.
// ⚠️ Casamento EXATO, nunca startsWith('Gerente'): `Gerente Adm` e
// `Gerente Plantao` não entram pelo `userRole === 'Gerente'` (o Adm é do
// ESCRITÓRIO e o escopoLojaSql não recorta escritório — ele veria/preencheria
// pela rede inteira). `Gerente Adm` TEM acesso ao módulo, mas por outro motivo
// (ver nota abaixo) — não confundir os dois `Gerente*`.
// ⚠️⚠️ `Auditoria` e `Gerente Adm` entraram em 2026-09-23 (DEM-00011, Fase 4,
// T21) — SÓ por causa do Checklist (agora "Auditoria Financeira e de
// Estoques"), que mudou do módulo Auditoria para cá. Preservado da lista que
// valia lá (`Auditoria.js › PERMISSIONS.forms`, antes da migração).
// ⚠️⚠️ `Treinamento` entrou no mesmo dia (T22), mesmo motivo: a Avaliação de
// Segurança do Alimento (ex-Auditoria SA) mudou de módulo, e o perfil já
// alcançava a ferramenta lá.
const canSeeOperacional = userRole === 'Gerente'
  || ((userRole === 'Operacional' || isAdmin || userRole === 'Diretoria' || userRole === 'Auditoria'
        || userRole === 'Gerente Adm' || userRole === 'Treinamento' || alcancaModulo(userRole, 'operacional')) && !presoLoja);
// Módulo Biblioteca (2026-09-04): os manuais de PROCESSO publicados no portal
// + a Videoteca (os vídeos de procedimento das telas de trabalho) + o Conecta
// (a newsletter interna, DEM-00022).
// ⚠️ ABERTA A TODO LOGADO desde a DEM-00022 (30/09/2026), por isso `true` e não
//    mais uma lista de perfis: é leitura de documentação da rede inteira, sem
//    dado recortado por loja (FORA do `presoLoja` de propósito), e quem publica
//    o Conecta (Marketing) precisa ver o resultado. Espelha a rota `/biblioteca`
//    do App.js, que também perdeu o `allowedRoles` — divergir dá menu sem tela
//    ou tela sem menu.
// ⚠️ Os PERFIS RESTRITOS não passam por este `show`: o menu deles é montado pelo
//    `cfgRestrito.permite` (abaixo). `Presidencia` (só /projetos) e `TI` (só
//    /chamados) ficam de fora; `Coordenador de Qualidade` entra porque tem
//    `/biblioteca` no `permite` dele. É assim que "todos exceto Presidência"
//    não vaza para os restritos.
const canSeeBiblioteca = true;
// Módulo Projetos (Inventário Executivo do CEO): ÚNICO caso de allowlist por
// USUÁRIO — o perfil dele (Diretoria) tem 8 pessoas. Regra em utils/acessoProjetos.js.
const canSeeProjetos = podeVerProjetos(user);
//const canSeeMapaCaixa = userRoleID === 57;

const navItems = [
    { path: '/home',            icon: <Home size={20} />,            label: 'Home',               show: true },
    { path: '/admin/dashboard', icon: <LayoutDashboard size={20} />, label: 'Painel Admin',    show: canSeePainelAdmin },
    { path: '/biblioteca',      icon: <BookOpen size={20} />,        label: 'Biblioteca',         show: canSeeBiblioteca },
    { path: '/chamados',        icon: <LifeBuoy size={20} />,        label: 'Chamados',           show: canSeeChamados },
    { path: '/compras',         icon: <ShoppingCart size={20} />,    label: 'Compras',            show: canSeeCompras },
    { path: '/contabilidade',   icon: <Calculator size={20} />,      label: 'Contábil',           show: canSeeContabil },
    { path: '/demandas',        icon: <ListChecks size={20} />,      label: 'Demandas',           show: canSeeDemandas },
    { path: '/etl',             icon: <GitMerge size={20} />,        label: 'Gestão de ETL',    show: isAdmin },
    { path: '/financeiro',      icon: <PiggyBank size={20} />,       label: 'Financeiro',         show: canSeeFinanceiro },
    { path: '/fiscal',          icon: <Landmark size={20} />,        label: 'Fiscal',             show: canSeeFiscal },
    { path: '/logistica',       icon: <Truck size={20} />,           label: 'Logística',          show: canSeeLogistica },
    { path: '/marketing',       icon: <Megaphone size={20} />,       label: 'Marketing',          show: canSeeMarketing },
    { path: '/manutencao',      icon: <HardHat size={20} />,         label: 'Manutenção',         show: canSeeManutencao },
    { path: '/medicina',        icon: <Stethoscope size={20} />,     label: 'Medicina do Trabalho',    show: canSeeMedicina },
    { path: '/operacional',     icon: <Boxes size={20} />,           label: 'Operacional',        show: canSeeOperacional },
    { path: '/processoseletivo',icon: <Briefcase size={20} />,       label: 'Processo Seletivo',show: canSeeProcessoSeletivo },
    { path: '/projetos',        icon: <ClipboardList size={20} />,   label: 'Projetos',           show: canSeeProjetos },
    { path: '/rh',              icon: <Users size={20} />,           label: 'Recursos Humanos',   show: canSeeRH },
    { path: '/treinamento',     icon: <GraduationCap size={20} />,   label: 'Treinamento', show: canSeeTreinamento },
];

// Perfil restrito (ex.: Coordenador de Qualidade): o menu mostra APENAS os
// módulos do allowlist — sem Home, sem mais nada. Para os demais perfis, o
// menu segue o `show` de cada item (permissões normais).
//
// ⚠️ O `show` é IGNORADO de propósito no ramo restrito: `canSeeFinanceiro` e
//    `canSeeRH` não citam o `Coordenador de Qualidade` (ele entra nesses dois
//    módulos por sub-módulo, não pelo hub), e exigir o `show` apagaria o menu
//    dele inteiro.
// ⚠️⚠️ MENOS o piloto do Chamados, que TEM de valer aqui também: ele responde
//    "o módulo já chegou nesta loja?", que é DISPONIBILIDADE e não permissão —
//    e ignorá-lo daria, no dia em que a `LOJAS_PILOTO` mudar, um item de menu
//    que leva a 403. Hoje os 22 coordenadores ativos estão em MAL/PCS/FER, as
//    três lojas do piloto, então isto não muda nada na tela: é a trava para
//    depois. Ver `chamados-piloto.js`.
const cfgRestrito = getConfigRestrito(userRole);
const itensMenu = cfgRestrito
    ? navItems.filter((item) => cfgRestrito.permite.includes(item.path)
        && (item.path !== '/chamados' || canSeeChamados))
    : navItems.filter((item) => item.show);

    return itensMenu;
}

/**
 * Para onde mandar um usuário que não tem a Home (ou que chegou nela sem nada
 * para ver): o PRIMEIRO módulo do menu dele que não seja a própria Home.
 *
 * ⚠️ Pode devolver `null` — usuário sem nenhum módulo. Quem chama decide o que
 *    fazer com isso; redirecionar para lugar nenhum daria laço de navegação.
 */
export function primeiraRotaDoUsuario(user, caps = new Set()) {
    const item = itensDeMenu(user, caps).find((i) => i.path !== '/home');
    return item ? item.path : null;
}

